import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle, GripVertical, MapPin, Truck, User } from 'lucide-react';
import { capitalizeFirstWord, formatTimelineTime } from '@peles/shared';
import type { TimelineEntry } from '@peles/shared';
import { EntryRowActions } from './EntryRowActions';

interface TimelineEntryRowProps {
  entry: TimelineEntry;
  /** Its time is earlier than a timed entry above it. */
  outOfOrder?: boolean;
  /** Read-only: no handle, no actions. */
  readOnly?: boolean;
  onEdit?: (entry: TimelineEntry) => void;
  onDelete?: (entry: TimelineEntry) => void;
  onAddBelow?: (entryId: string) => void;
}

/**
 * One cue of the run of show, on the section's time rail: the time, a dot
 * (hollow for an entry with no time), what happens, and where / who / the
 * vendor note as small labels, the notes beneath.
 */
export function TimelineEntryRow({
  entry,
  outOfOrder = false,
  readOnly = false,
  onEdit,
  onDelete,
  onAddBelow,
}: TimelineEntryRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.id,
    disabled: readOnly,
  });
  const time = formatTimelineTime(entry.time);

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className="group relative flex items-stretch gap-2 rounded-md hover:bg-gray-50 focus-within:bg-gray-50"
      data-testid={`entry-${entry.id}`}
    >
      {!readOnly && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="self-center flex items-center justify-center min-h-11 w-6 sm:min-h-0 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none shrink-0"
          aria-label={`Drag ${entry.title}`}
          data-testid={`drag-entry-${entry.id}`}
        >
          <GripVertical size={14} />
        </button>
      )}

      <div
        className={`w-14 sm:w-[4.75rem] shrink-0 pt-2 text-right font-mono text-xs sm:text-sm tabular-nums ${
          time ? 'text-primary-700 font-semibold' : 'text-gray-300'
        }`}
        data-testid={`entry-time-${entry.id}`}
      >
        {time || '—'}
      </div>

      {/* The rail: a dot on a line that runs on to the next cue. */}
      <div className="relative w-3 shrink-0 flex justify-center" aria-hidden>
        <span className="absolute inset-y-0 w-px bg-gray-200" />
        <span
          className={`relative mt-3 h-2.5 w-2.5 rounded-full ${
            outOfOrder
              ? 'bg-amber-500'
              : time
                ? 'bg-primary-500'
                : 'bg-white border-2 border-gray-300'
          }`}
        />
      </div>

      <div className="flex-1 min-w-0 py-1.5">
        <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
          <span className="text-sm font-medium text-gray-900 break-words">
            {capitalizeFirstWord(entry.title)}
          </span>
          {outOfOrder && (
            <span
              className="inline-flex items-center gap-1 shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
              title="This time is earlier than an entry above it"
              data-testid={`entry-out-of-order-${entry.id}`}
            >
              <AlertTriangle size={11} aria-hidden />
              Out of order
            </span>
          )}
        </div>
        {(entry.location || entry.vendorNote || entry.assignedToName) && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {entry.location && (
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                <MapPin size={11} aria-hidden />
                {entry.location}
              </span>
            )}
            {entry.vendorNote && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                <Truck size={11} aria-hidden />
                {entry.vendorNote}
              </span>
            )}
            {entry.assignedToName && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700">
                <User size={11} aria-hidden />
                {entry.assignedToName}
              </span>
            )}
          </div>
        )}
        {entry.notes && (
          <p className="mt-1 text-xs text-gray-500 whitespace-pre-line break-words">
            {entry.notes}
          </p>
        )}
      </div>

      {!readOnly && onEdit && onDelete && onAddBelow && (
        <div className="self-start pt-0.5">
          <EntryRowActions
            entryId={entry.id}
            onAddBelow={onAddBelow}
            onEdit={() => onEdit(entry)}
            onDelete={() => onDelete(entry)}
          />
        </div>
      )}
    </div>
  );
}
